import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { z } from "zod";
import { apiGet, asOverrideKey } from "@/lib/api-client";
import { claimKeys } from "@/lib/query";
import { AgencyDetailSchema } from "@/schemas/agent";

// Two shapes — the API withholds the payload entirely when the claim is
// restricted from the current user; otherwise the full detail comes through.
const ClaimDetailRestrictedSchema = z.object({
  claimNumber: z.string(),
  isRestricted: z.literal(true),
  restrictedMessage: z.string().nullable(),
});

const ClaimDetailUnrestrictedSchema = z.object({
  claimNumber: z.string(),
  isRestricted: z.literal(false),
  lossTypeDescription: z.string().nullable(),
  policyNumber: z.string().nullable(),
  agentNumber: z.string().nullable(),
  lossDate: z.string().nullable(),
  reportDate: z.string().nullable(),
  currentStatus: z.string().nullable(),
  claimStatusDescription: z.string().nullable(),
  memicAttorneyName: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  claimantAddress: z.string().nullable(),
  claimantCity: z.string().nullable(),
  claimantState: z.string().nullable(),
  claimantZipCode: z.string().nullable(),
  claimantPhone: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  adjusterName: z.string().nullable(),
  injuryDescription: z.string().nullable(),
  physicianName: z.string().nullable(),
  employeeWorkCapacity: z.string().nullable(),
  rtwLightDuty: z.string().nullable(),
  rtwFullDuty: z.string().nullable(),
  dateOfIncapacity: z.string().nullable(),
  lastActiveDate: z.string().nullable(),
  permanentImpairmentExpected: z.string().nullable(),
  mmiReached: z.string().nullable(),
  currentDiagnosis: z.string().nullable(),
  actionPlan: z.string().nullable(),
  attorneyName: z.string().nullable(),
  injuredWorkerAttorney: z.string().nullable(),
  occupationDescription: z.string().nullable(),
  educationLevel: z.string().nullable(),
  averageWeeklyWage: z.number().nullable(),
  compensationRate: z.number().nullable(),
  currentTotalCompensationRatePaid: z.number().nullable(),
  piRating: z.number().nullable(),
});

const ClaimDetailSchema = z.discriminatedUnion("isRestricted", [
  ClaimDetailRestrictedSchema,
  ClaimDetailUnrestrictedSchema,
]);

const ClaimFinancialRowSchema = z.object({
  type: z.string(),
  paid: z.number(),
  reserved: z.number(),
  recovered: z.number(),
  outstanding: z.number(),
  netIncurred: z.number(),
});

const ClaimFinancialsSchema = z.object({
  byType: z.array(ClaimFinancialRowSchema),
  totals: z.object({
    paid: z.number(),
    reserved: z.number(),
    recovered: z.number(),
    outstanding: z.number(),
    netIncurred: z.number(),
  }),
});

const ClaimPolicySchema = z
  .object({
    claimPolicy: z.object({
      policyNumber: z.string(),
      effectiveDate: z.union([z.string(), z.date()]).nullable(),
    }),
    policy: z
      .object({
        policyNumber: z.string(),
        insuredName: z.string().nullable(),
        inceptionDate: z.string().nullable(),
        expirationDate: z.string().nullable(),
      })
      .nullable(),
    insured: z
      .object({
        insuredName: z.string().nullable(),
        insuredAddress: z.string().nullable(),
      })
      .nullable(),
  })
  .nullable();

const ClaimAgencySchema = AgencyDetailSchema.nullable();

const ClaimsMrpRowSchema = z.object({
  rowId: z.string(),
  claimNumber: z.string(),
  isRestricted: z.boolean(),
  currentStatus: z.string().nullable(),
  claimStatusDescription: z.string().nullable(),
  lossTypeDescription: z.string().nullable(),
  lossDate: z.string().nullable(),
  adjusterName: z.string().nullable(),
  claimManagerName: z.string().nullable(),
  accidentState: z.string().nullable(),
  injuryDescription: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  policyNumber: z.string().nullable(),
  agentNumber: z.string().nullable(),
  reserve: z.number(),
  paid: z.number(),
  recovery: z.number(),
  incurred: z.number(),
});

const ClaimsMrpAggregatesSchema = z.object({
  incurred: z.number(),
  paid: z.number(),
  reserve: z.number(),
  openCount: z.number(),
  closedCount: z.number(),
  indemnityCount: z.number(),
  medOnlyCount: z.number(),
});

const ClaimsMrpPageSchema = z.object({
  data: z.array(ClaimsMrpRowSchema),
  nextCursor: z.string().nullable(),
  prevCursor: z.string().nullable(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
  total: z.number(),
  aggregates: ClaimsMrpAggregatesSchema,
});

const ClaimsMrpFacetsSchema = z.object({
  statuses: z.array(z.string()),
  lossTypes: z.array(z.string()),
  states: z.array(z.string()),
  adjusters: z.array(z.string()),
});

export type ClaimDetail = z.infer<typeof ClaimDetailSchema>;
export type ClaimFinancialRow = z.infer<typeof ClaimFinancialRowSchema>;
export type ClaimFinancials = z.infer<typeof ClaimFinancialsSchema>;
export type ClaimsMrpRow = z.infer<typeof ClaimsMrpRowSchema>;
export type ClaimsMrpAggregates = z.infer<typeof ClaimsMrpAggregatesSchema>;
export type ClaimsMrpPage = z.infer<typeof ClaimsMrpPageSchema>;
export type ClaimsMrpFacets = z.infer<typeof ClaimsMrpFacetsSchema>;

// On home-page mount, re-validate the restriction state of each cached recent
// claim so that any claim that became restricted since the user last saw it
// shows as "Restricted" instead of surfacing the stale cached claimant name
// from localStorage. Returns the set of claim numbers that are currently
// restricted for the active user; callers overlay that on the display.
const RestrictionCheckResultSchema = z.object({
  restricted: z.array(z.string()),
});

export function useClaimRestrictionCheck(numbers: readonly string[]) {
  // Sort + dedupe so the same recent list (regardless of order) hits one
  // cache entry. Stable key avoids re-fetches when other home-page state
  // changes mutate the recent array reference.
  const key = useMemo(() => Array.from(new Set(numbers)).sort(), [numbers]);
  return useQuery({
    queryKey: [...claimKeys.restrictionCheck(key), asOverrideKey()],
    queryFn: async () => {
      const res = await fetch("/api/claims/restriction-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ numbers: key }),
      });
      if (!res.ok) {
        return { restricted: [] as string[] };
      }
      const body = await res.json();
      const parsed = RestrictionCheckResultSchema.safeParse(body?.data ?? body);
      return parsed.success ? parsed.data : { restricted: [] as string[] };
    },
    enabled: key.length > 0,
    staleTime: 60_000,
  });
}

export function useClaim(claimNumber: string) {
  return useQuery({
    queryKey: [...claimKeys.detail(claimNumber), asOverrideKey()],
    queryFn: () => apiGet(`/api/claims/${encodeURIComponent(claimNumber)}`, ClaimDetailSchema),
  });
}

export function useClaimFinancials(claimNumber: string, enabled = true) {
  return useQuery({
    queryKey: [...claimKeys.financials(claimNumber), asOverrideKey()],
    queryFn: () =>
      apiGet(`/api/claims/${encodeURIComponent(claimNumber)}/financials`, ClaimFinancialsSchema),
    enabled,
  });
}

export function useClaimPolicy(claimNumber: string, enabled = true) {
  return useQuery({
    queryKey: [...claimKeys.policy(claimNumber), asOverrideKey()],
    queryFn: () =>
      apiGet(`/api/claims/${encodeURIComponent(claimNumber)}/policy`, ClaimPolicySchema),
    enabled,
  });
}

export function useClaimAgency(claimNumber: string, enabled = true) {
  return useQuery({
    queryKey: [...claimKeys.agency(claimNumber), asOverrideKey()],
    queryFn: () =>
      apiGet(`/api/claims/${encodeURIComponent(claimNumber)}/agency`, ClaimAgencySchema),
    enabled,
  });
}

/** Infinite-scroll style: forward-only `Load more` pagination. The query
 *  starts on page 1 (no cursor), then `fetchNextPage` reuses the cursor that
 *  the previous page returned. Each page also carries a `total`, so the page
 *  can show "X claims" regardless of how much has been loaded.
 *
 *  Page size matches the app-wide "Load 25 more" convention. */
export function useClaimsMrp(filters: Record<string, string | undefined>) {
  return useInfiniteQuery({
    queryKey: [...claimKeys.mrp(filters), asOverrideKey()],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) {
        if (v) params.set(k, v);
      }
      // 100 (the server's default page size) rather than 25: the MRP view
      // auto-loads every page so the per-column filter menus and client-side
      // refinement see the full result, and a larger page means that drain is
      // a couple of round-trips instead of ten on a multi-hundred-row book.
      params.set("limit", "100");
      if (pageParam) params.set("cursor", pageParam);
      return apiGet(`/api/claims/mrp?${params.toString()}`, ClaimsMrpPageSchema);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

/** Distinct values for the Claims MRP per-column filter menus, over the full
 *  claims domain (not just the loaded rows). Lets the menus offer every value
 *  a user could filter by — e.g. Status always lists Open/Closed/Reopen even
 *  when the current view shows only open claims. */
export function useClaimsMrpFacets() {
  return useQuery({
    queryKey: [...claimKeys.mrpFacets, asOverrideKey()],
    queryFn: () => apiGet("/api/claims/mrp/facets", ClaimsMrpFacetsSchema),
    // The domain is stable across a session; no need to refetch on every mount.
    staleTime: 5 * 60_000,
  });
}
